import React, { useEffect, useRef, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Info, Minus, MousePointer2, X } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';

// The team tabs' look (Nic 2026-10-25): the /billing/envoy page's design
// language. Off-white canvas, white rounded-2xl cards with the
// shadow-as-border recipe, a dark hero scorecard with soft glow blobs, and
// short flip tiles. Copied from EnvoyDashboardPage.js (its atoms are local
// to that page) with dark twins added.

export const CARD_SHADOW =
  'shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04),0_8px_16px_-8px_rgba(15,23,42,0.06)] dark:shadow-none dark:ring-1 dark:ring-white/10';
export const SURFACE = 'bg-white dark:!bg-surface-card';

// One hue per meaning, used by icons, tiles and chart series alike.
export const HUES = {
  indigo: '#6366F1',
  emerald: '#10B981',
  orange: '#F97316',
  sky: '#0EA5E9',
  rose: '#F43F5E',
  violet: '#8B5CF6',
  amber: '#F59E0B',
  teal: '#14B8A6',
  slate: '#94A3B8',
};

// Each person keeps one color everywhere on a tab (their stacked bar, their
// avatar). Ordered so neighbors never look alike.
const PEOPLE_HUES = ['#6366F1', '#10B981', '#F97316', '#0EA5E9', '#8B5CF6', '#F43F5E', '#14B8A6', '#EAB308'];
export const personColors = (people) => {
  const out = { none: HUES.slate };
  (people || []).forEach((p, i) => {
    out[p.userId] = PEOPLE_HUES[i % PEOPLE_HUES.length];
  });
  return out;
};

// Instant hover tooltip (Envoy recipe: no native-title delay), on Radix
// Tooltip since 2026-10-25 (Nic: "the right way"): it stays attached and on
// screen through resizes, renders above the page so a table or tile can't
// clip it or grow around it, opens on hover and keyboard focus, closes on
// Escape, and is announced to screen readers. A tap toggles it too (phones
// have no hover), and never reaches a flip tile underneath.
//   align  'center' | 'left' | 'right' (which edge of the icon it lines up on)
//   below  open under the icon instead of above (it flips on its own when
//          there's no room either way)
//   wide   a wrapping paragraph instead of a one-line pill
const ALIGN = { center: 'center', left: 'start', right: 'end' };
export const HoverTip = ({ label, children, wide = false, align = 'center', below = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label={typeof label === 'string' ? label : undefined}
            className={cn(
              'inline-flex shrink-0 cursor-default rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
              open ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400',
            )}
            // Radix closes a tooltip on press; a tap here means "show it".
            onPointerDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            // Enter or Space opens it here, and never flips the tile below.
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setOpen((v) => !v);
              }
            }}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={below ? 'bottom' : 'top'}
          align={ALIGN[align] || 'center'}
          className={cn(
            wide
              ? 'w-64 px-3 py-2 text-left text-[11px] font-normal normal-case leading-4 tracking-normal'
              : 'whitespace-nowrap normal-case tracking-normal',
          )}
        >
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const InfoTip = ({ label, align = 'center', below = false }) => (
  <HoverTip label={label} wide align={align} below={below}>
    <Info size={13} strokeWidth={2} />
  </HoverTip>
);

// White card. `eyebrow` = the small uppercase line, `title` = the bold one.
export const Card = ({ eyebrow, title, info, right, children, className = '' }) => (
  <div className={cn('relative rounded-2xl p-5', SURFACE, CARD_SHADOW, className)}>
    {eyebrow || title || right ? (
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{eyebrow}</div>
          ) : null}
          {title ? (
            <div className="mt-0.5 flex items-center gap-1.5 text-base font-semibold text-slate-900 dark:text-white">
              {title}
              {info ? <InfoTip label={info} align="left" /> : null}
            </div>
          ) : null}
        </div>
        {right ? <div className="flex flex-wrap items-center gap-2">{right}</div> : null}
      </div>
    ) : null}
    {children}
  </div>
);

export const Skeleton = ({ className = '' }) => (
  <div className={cn('animate-pulse rounded-2xl bg-slate-200/60 dark:bg-white/5', className)} />
);

// Click-to-flip (Envoy/Cadence recipe): pure CSS 3D, each face rotates on its
// own. The FRONT drives the height; the back covers it and must fit.
// `resetKey` flips back to the front whenever it changes (a new period).
// `lazyBack`: the back isn't built until the first flip (Nic 2026-10-25:
// resizing the window lagged, a white band trailing the edge, because every
// tile kept a hidden chart that re-measured and redrew on each pixel).
export const FlipCard = ({ front, back, className = '', resetKey = null, label, lazyBack = false }) => {
  const [flipped, setFlipped] = useState(false);
  const [everFlipped, setEverFlipped] = useState(false);
  useEffect(() => {
    setFlipped(false);
  }, [resetKey]);
  const toggle = () => {
    setEverFlipped(true);
    setFlipped((v) => !v);
  };
  // The face turned away is `inert`, not just aria-hidden (Nic 2026-10-25):
  // clicking a chart focused its SVG, and hiding a face that still held
  // focus made the browser warn ("Blocked aria-hidden..."). Inert hides it
  // AND moves focus out. Set by ref: React 18 has no `inert` prop.
  const cardRef = useRef(null);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  useEffect(() => {
    const set = (el, off) => {
      if (!el) return;
      if (off) {
        // Focus inside the face going away goes to the card itself, so a
        // keyboard user keeps their place.
        if (el.contains(document.activeElement)) cardRef.current?.focus({ preventScroll: true });
        el.setAttribute('inert', '');
      } else el.removeAttribute('inert');
    };
    set(frontRef.current, flipped);
    set(backRef.current, !flipped);
  }, [flipped, everFlipped]);
  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={label ? `${label}: ${flipped ? 'show the number' : 'show the chart'}` : undefined}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      className={cn(
        'cursor-pointer select-none rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
        className,
      )}
    >
      <div className="relative h-full [perspective:1400px]">
        <div
          ref={frontRef}
          className={cn(
            'h-full transition-transform duration-500 [backface-visibility:hidden] [will-change:transform]',
            flipped ? '[transform:rotateY(180deg)]' : '[transform:rotateY(0deg)]',
          )}
        >
          {front}
        </div>
        <div
          ref={backRef}
          className={cn(
            'absolute inset-0 overflow-hidden rounded-2xl transition-transform duration-500 [backface-visibility:hidden] [will-change:transform]',
            flipped ? '[transform:rotateY(0deg)]' : '[transform:rotateY(-180deg)]',
          )}
        >
          {!lazyBack || everFlipped ? back : null}
        </div>
      </div>
    </div>
  );
};

export const FlipHint = ({ close = false }) => (
  <span aria-hidden className="pointer-events-none absolute bottom-3.5 right-3.5 text-slate-500">
    {close ? <X size={17} strokeWidth={2.4} /> : <MousePointer2 size={17} strokeWidth={2.4} />}
  </span>
);

// Dark hero scorecard with the glow blobs (same in both themes).
export const HeroCard = ({ children, hint, glow = 'emerald' }) => (
  <div className="relative h-full overflow-hidden rounded-2xl bg-[#0E121B] p-6 shadow-[0_8px_30px_rgba(15,23,42,0.25)] dark:ring-1 dark:ring-white/10">
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute -left-16 -top-24 h-64 w-64 rounded-full blur-3xl',
        glow === 'violet' ? 'bg-violet-500/20' : 'bg-emerald-500/20',
      )}
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full bg-sky-500/15 blur-3xl"
    />
    <div className="relative h-full">{children}</div>
    {hint}
  </div>
);

// A progress ring against a goal: green at or past it, amber under, with a
// white tick where the goal sits. No goal = sky.
export const Ring = ({ value, target = null, label, sub, size = 124, stroke = 11 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  const hit = target != null && value != null && value >= target;
  const color = target == null ? HUES.sky : hit ? HUES.emerald : HUES.amber;
  const tick = target != null ? (target / 100) * 2 * Math.PI : null;
  const at = (rad) => [cx + rad * Math.sin(tick), cx - rad * Math.cos(tick)];
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} aria-hidden>
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
          {value != null ? (
            <circle
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${(v / 100) * c} ${c}`}
              transform={`rotate(-90 ${cx} ${cx})`}
              style={{ transition: 'stroke-dasharray 700ms ease-out' }}
            />
          ) : null}
          {tick != null ? (
            <line
              x1={at(r - stroke / 2 - 3)[0]}
              y1={at(r - stroke / 2 - 3)[1]}
              x2={at(r + stroke / 2 + 3)[0]}
              y2={at(r + stroke / 2 + 3)[1]}
              stroke="white"
              strokeWidth={2.5}
              strokeLinecap="round"
            />
          ) : null}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold tabular-nums leading-none text-white">
            {value == null ? '-' : `${Math.round(value)}%`}
          </span>
          {target != null ? (
            <span className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              goal {target}%
            </span>
          ) : null}
        </div>
      </div>
      <div className="text-center">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">{label}</div>
        {sub ? <div className="text-[11px] text-slate-500">{sub}</div> : null}
      </div>
    </div>
  );
};

// Change against the period before. kind: 'count' | 'pts' | 'days' |
// 'minutes'. `invert` = lower is better (waits, days). Nothing to compare
// (either side missing) renders nothing.
export const deltaText = (value, prev, kind = 'count') => {
  if (value == null || prev == null) return null;
  const diff = Math.round((value - prev) * 10) / 10;
  const sign = diff > 0 ? '+' : '';
  const unit = kind === 'pts' ? ' pts' : kind === 'days' ? ' days' : kind === 'minutes' ? ' min' : '';
  return { diff, text: `${sign}${diff.toLocaleString('en-US')}${unit}` };
};

export const Delta = ({ value, prev, kind = 'count', invert = false, vs, title, dark = false }) => {
  const d = deltaText(value, prev, kind);
  if (!d) return null;
  const good = invert ? d.diff < 0 : d.diff > 0;
  const tone =
    d.diff === 0
      ? dark
        ? 'text-slate-400'
        : 'text-slate-500'
      : good
        ? dark
          ? 'text-emerald-400'
          : 'text-emerald-600'
        : dark
          ? 'text-rose-400'
          : 'text-rose-600';
  const Icon = d.diff === 0 ? Minus : d.diff > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      title={title}
      className={cn('inline-flex items-center gap-0.5 whitespace-nowrap text-xs font-semibold tabular-nums', tone)}
    >
      <Icon size={13} strokeWidth={2.5} />
      {d.diff === 0 ? 'Same' : d.text}
      {vs ? (
        <span className={cn('ml-1 font-normal', dark ? 'text-slate-500' : 'text-slate-400')}>{vs}</span>
      ) : null}
    </span>
  );
};

// Stat tile = a short flip card. Front: icon, label, the number, and the
// change. Back: `back` (a chart over the period, or a breakdown).
const TILE_H = 'h-[10.25rem]';

export const StatTile = ({
  icon: Icon,
  tint,
  accent = 'text-slate-500',
  label,
  value,
  foot,
  info,
  back,
  backCaption,
  flipKey,
}) => {
  const display = typeof value === 'number' ? value.toLocaleString('en-US') : String(value ?? '-');
  const sizeCls = display.length > 7 ? 'text-3xl' : display.length > 5 ? 'text-4xl' : 'text-[2.75rem]';
  const front = (
    <div className={cn('relative flex flex-col rounded-2xl p-4', TILE_H, SURFACE, CARD_SHADOW)}>
      <div className="flex items-center gap-2.5 pr-5">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tint)}>
          <Icon size={16} strokeWidth={2} />
        </span>
        <span className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-slate-400">
          {label}
        </span>
      </div>
      {info ? (
        <span className="absolute right-3 top-3">
          <InfoTip label={info} align="right" />
        </span>
      ) : null}
      <div className="flex flex-1 items-center justify-center">
        <span
          className={cn(
            sizeCls,
            'font-semibold leading-none tracking-tight tabular-nums text-slate-900 dark:text-white',
          )}
        >
          {display}
        </span>
      </div>
      <div className="flex min-h-[1rem] items-center justify-center pr-4 text-[11px] text-slate-400">{foot}</div>
      <span aria-hidden className="pointer-events-none absolute bottom-3.5 right-3.5 text-slate-300 dark:text-slate-600">
        <MousePointer2 size={13} strokeWidth={2.4} />
      </span>
    </div>
  );
  const backFace = (
    <div className={cn('relative flex flex-col rounded-2xl px-4 pb-3 pt-4', TILE_H, SURFACE, CARD_SHADOW)}>
      <div className="flex shrink-0 items-baseline gap-2 pr-5">
        <span className={cn('text-[11px] font-semibold uppercase leading-tight tracking-wider', accent)}>{label}</span>
        {backCaption ? <span className="truncate text-[11px] text-slate-400">{backCaption}</span> : null}
      </div>
      <div className="mt-2 min-h-0 flex-1">{back}</div>
      <span aria-hidden className="pointer-events-none absolute right-3.5 top-3.5 text-slate-300 dark:text-slate-600">
        <X size={13} strokeWidth={2.4} />
      </span>
    </div>
  );
  return <FlipCard front={front} back={backFace} resetKey={flipKey} label={label} lazyBack />;
};

// A breakdown back (Envoy tile recipe): one thin bar with the split, the
// colored numbers as its legend. `stats` = [{ label, value, color }].
export const StatsBack = ({ stats }) => {
  const total = stats.reduce((s, x) => s + (Number(x.value) || 0), 0);
  return (
    <div className="flex h-full flex-col">
      <div className="mt-2 flex h-2 w-full shrink-0 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
        {total > 0
          ? stats
              .filter((s) => Number(s.value) > 0)
              .map((s) => (
                <span
                  key={s.label}
                  className="h-full"
                  style={{ flex: `${Number(s.value)} 1 0%`, minWidth: 8, backgroundColor: s.color }}
                />
              ))
          : null}
      </div>
      <div className="flex flex-1 flex-wrap content-center items-center justify-center gap-x-5 gap-y-1.5 pt-2">
        {stats.map((s) => (
          <span key={s.label} className="inline-flex items-baseline gap-1.5 whitespace-nowrap text-[11px] text-slate-500">
            <b className="text-sm font-semibold tabular-nums leading-none" style={{ color: s.color }}>
              {Number(s.value || 0).toLocaleString('en-US')}
            </b>
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// Callout beside a chart (the V3 deck's side tiles): a colored edge, the
// label, the number, and its change.
export const Callout = ({ color, label, value, children }) => (
  <div
    className="rounded-xl bg-slate-50 py-3 pl-4 pr-3 dark:bg-white/5"
    style={{ boxShadow: `inset 3px 0 0 ${color}` }}
  >
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
    <div className="mt-0.5 text-2xl font-semibold tabular-nums leading-tight tracking-tight text-slate-900 dark:text-white">
      {value}
    </div>
    {children ? <div className="mt-0.5 text-[11px] text-slate-400">{children}</div> : null}
  </div>
);

// Initials in the person's color.
export const Avatar = ({ name, color }) => {
  const initials = String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
      style={{ backgroundColor: color || HUES.slate }}
    >
      {initials}
    </span>
  );
};

// A number against a goal as a soft pill: green at or past it, amber under.
export const GoalPill = ({ value, target, children }) => {
  if (value == null) return <span className="text-slate-300 dark:text-slate-600">-</span>;
  const hit = value >= target;
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
        hit
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
          : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
      )}
    >
      {children ?? `${Math.round(value)}%`}
    </span>
  );
};

export const Th = ({ children, className = '', info, align = 'right' }) => (
  <th
    className={cn(
      'whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400',
      align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right',
      className,
    )}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {info ? <InfoTip label={info} align="right" below /> : null}
    </span>
  </th>
);
